import React from 'react';
import '../../styles/components/doctor/DoctorStatusGuard.css';
import restrictedIcon from '../../assets/logo/doc_status.svg';

/**
 * DoctorStatusGuard - Controls access to features based on doctor verification status
 * @param {Object} props
 * @param {ReactNode} props.children - The content to render if access is granted
 * @param {string} props.requiredStatus - 'approved' | 'pending' | 'any'
 * @param {Object} props.user - Current user object
 * @param {ReactNode} props.fallback - What to show if access is denied
 */
const DoctorStatusGuard = ({ 
  children, 
  requiredStatus = 'approved', 
  user, 
  fallback = null 
}) => {
  // Check if user exists and is a doctor
  if (!user || user.role !== 'doctor') {
    return fallback;
  }

  // Allow any status
  if (requiredStatus === 'any') {
    return children;
  }

  // Check for pending status
  if (requiredStatus === 'pending') {
    const isPending = user.accountStatus === 'pending' || user.verificationStatus === 'pending';
    return isPending ? children : fallback;
  }

  // Check for approved status (default)
  if (requiredStatus === 'approved') {
    const isApproved = user.accountStatus === 'active' && user.verificationStatus === 'approved';
    return isApproved ? children : fallback;
  }

  return fallback;
};

/**
 * RestrictedFeatureMessage - Shows when a feature is restricted
 */
export const RestrictedFeatureMessage = ({ 
  title = "Feature Restricted", 
  message = "This feature is available after your account is verified.",
  showUpgrade = true 
}) => (
  <div className="restricted-feature-message">
    <div className="restricted-icon">
      <img src={restrictedIcon} alt="Restricted" />
    </div>
    <p>{message}</p>
    {showUpgrade && (
      <div className="upgrade-notice">
        <p>Complete your onboarding and await verification to unlock all features.</p>
      </div>
    )}
  </div>
);

/**
 * StatusBanner - Shows current verification status
 */
export const StatusBanner = ({ user }) => {
  if (!user || user.role !== 'doctor') return null;

  const isPending = user.accountStatus === 'pending' || user.verificationStatus === 'pending';
  const isApproved = user.accountStatus === 'active' && user.verificationStatus === 'approved';

  if (isApproved) return null; // Don't show banner for approved doctors

  const getStatusInfo = () => {
    if (user.accountStatus === 'pending') {
      return {
        type: 'warning',
        title: 'Account Pending Verification',
        message: 'Your professional credentials are being reviewed. This typically takes 1-3 business days.',
        daysRemaining: 30 // You can calculate this based on registration date
      };
    }
    
    if (user.verificationStatus === 'pending') {
      return {
        type: 'info',
        title: 'Verification In Progress',
        message: 'Please complete your onboarding if you haven\'t done so already.',
        daysRemaining: 30
      };
    }

    return {
      type: 'warning',
      title: 'Account Setup Required',
      message: 'Complete your professional profile to start helping patients.',
      daysRemaining: 30
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <div className={`status-banner status-banner-${statusInfo.type}`}>
      <div className="status-banner-content">
        <div className="status-banner-header">
          <h4>{statusInfo.title}</h4>
          <span className="status-badge">{statusInfo.daysRemaining} days remaining</span>
        </div>
        <p>{statusInfo.message}</p>
        
        {/* Progress indicator */}
        <div className="status-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ 
                width: user.verificationStatus === 'pending' ? '50%' : '25%' 
              }}
            ></div>
          </div>
          <div className="progress-steps">
            <span className={user.verificationStatus ? 'completed' : 'current'}>
               Registered
            </span>
            <span className={user.verificationStatus === 'pending' ? 'current' : 'pending'}>
               Under Review
            </span>
            <span className="pending"> Approved</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorStatusGuard;